import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Independent ADMIN theme — separate from the public-site theme (`ek-theme`).
// While the admin workspace is mounted we drive the <html> `.dark` class from the
// admin preference (so shadcn tokens + `dark:` variants + the scoped `.ek-admin`
// remap all resolve correctly); on unmount we restore the public-site theme.
//
// Three-way mode:
//   - "auto"  → follow the OS `prefers-color-scheme` live (no persisted key)
//   - "light" / "dark" → explicit, persisted choice (OS tracking stops)
const KEY = "ek-admin-theme";
const SITE_KEY = "ek-theme";

function osPref() {
  try { return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"; }
  catch (e) { return "light"; }
}

function applyHtml(t) {
  const dark = t === "dark";
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}

// Persisted selection: "light" | "dark" if explicit, else "auto".
function storedMode() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "auto";
  } catch (e) { return "auto"; }
}

function resolveFrom(mode) {
  return mode === "auto" ? osPref() : mode;
}

function siteTheme() {
  try {
    const s = localStorage.getItem(SITE_KEY);
    if (s) return s;
    return osPref();
  } catch (e) { return "light"; }
}

export function useAdminTheme() {
  const [mode, setModeState] = useState(storedMode);
  const [resolved, setResolved] = useState(() => resolveFrom(storedMode()));

  useLayoutEffect(() => {
    const r = resolveFrom(storedMode());
    setResolved(r);
    applyHtml(r);
    return () => applyHtml(siteTheme());
  }, []);

  // In "auto" mode, follow the OS preference live.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch (e) { return undefined; }
    const onChange = () => {
      if (storedMode() !== "auto") return; // user made an explicit choice
      const r = osPref();
      setResolved(r);
      applyHtml(r);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const setMode = useCallback((next) => {
    try {
      if (next === "auto") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch (e) { /* private mode */ }
    setModeState(next);
    const r = resolveFrom(next);
    setResolved(r);
    applyHtml(r);
  }, []);

  return { mode, setMode, isDark: resolved === "dark", theme: resolved };
}
